/**
 * sl-8fwi.8 (#64 fix): Pure helper for the manual `handleMappingChange`
 * writer path in `field-mapping-grid.tsx`. Extracted from the React
 * useCallback closure so the spec can drive the SAME function the
 * component uses — pre-fix the only test coverage was the auto-match
 * pipeline (`auto-match-fields-display-name.spec.ts`), which never
 * exercised what happens when the user explicitly picks (or clears) a
 * source field via the dropdown.
 *
 * SOLID:
 * - S: Single responsibility — compute the next mapping array given a
 *      single column-id update. No side effects, no React, no notify.
 * - D: All collaborators (display-name resolver, schema fields) are
 *      injected; the function knows nothing about WorkflowService or
 *      i18n.
 *
 * The component still owns the React state plumbing (useCallback,
 * onMappingsChange) — this helper only owns the mapping math.
 *
 * sl-8fwi rework (AC3 strict validation): Pre-rework the helper
 * persisted unknown response-field paths with a fallback display name
 * resolved via `getDisplayName`. The deep audit flagged this as a
 * silent-failure path — the dropdown should never offer a path outside
 * `responseFields`, so one arriving here is either stale state or a
 * bug, and persisting it hides the root cause. The helper now rejects
 * unknown paths outright: the existing mapping is still removed
 * (explicit clear), but no new mapping is appended. The `getDisplayName`
 * parameter is kept for API compatibility but is no longer consulted
 * on the unknown-path branch.
 */

import type { OutputMapping } from '../../services/automation';

/** Schema field shape used by FieldMappingGrid (mirrors the local
 *  `SchemaField` interface so we don't import the entire component). */
export interface OutputMappingSchemaField {
  readonly path: string;
  readonly displayName: string;
  readonly type?: string;
}

export interface ApplyMappingChangeInput {
  /** Current mapping array (will not be mutated). */
  readonly prev: ReadonlyArray<OutputMapping>;
  /** Target column whose mapping is being updated. */
  readonly columnId: string;
  /** New response field path. Empty string clears the mapping. */
  readonly responseField: string;
  /** Target table id — required to insert a new mapping. Without it
   *  the helper only removes the existing mapping (defensive: a
   *  mapping cannot be persisted without a table reference). */
  readonly tableId: string | undefined;
  /** Schema fields available in the workflow output catalogue. */
  readonly responseFields: ReadonlyArray<OutputMappingSchemaField>;
  /** Historical display-name resolver. Pre-rework this was consulted when
   *  a path was not in `responseFields`. After the sl-8fwi rework
   *  (AC3 strict validation) it is NEVER consulted for unknown paths
   *  because unknown paths are rejected — it survives as a parameter
   *  only so existing callers keep compiling. */
  readonly getDisplayName: (path: string) => string;
}

/**
 * Compute the next OutputMapping array after the user explicitly picked
 * (or cleared) a source field for `columnId`. Removes any existing
 * mapping for the column and, when `responseField` + `tableId` are both
 * present AND `responseField` resolves to a real entry in
 * `responseFields`, appends the new one.
 *
 * The new mapping carries:
 *   - `responseFieldName`: snapshot of the workflow output field's
 *     `displayName` so the canvas-block renderer (sl-g3on.4.1) can
 *     show a human label without re-fetching the schema.
 *   - `matchConfigId`: preserved from the existing mapping for the
 *     same column so the relation-lookup MatchConfig stays intact when
 *     the user just changes the source field.
 *   - `fixedValue`: cleared because picking a response field replaces
 *     any previously-configured fixed value.
 */
export function applyMappingChange(
  input: ApplyMappingChangeInput
): OutputMapping[] {
  const { prev, columnId, responseField, tableId, responseFields } = input;

  const existingMapping = prev.find(m => m.targetColumnId === columnId);
  const filteredMappings = prev.filter(m => m.targetColumnId !== columnId);

  if (!responseField || !tableId) {
    return filteredMappings;
  }

  // sl-8fwi.8 rework (AC3): strict validation — the path MUST exist in
  // the cached schema. Unknown paths are rejected outright so the UI
  // state (dropdown) and the persisted state (mapping array) can never
  // diverge.
  const fieldInfo = responseFields.find(f => f.path === responseField);
  if (!fieldInfo) {
    return filteredMappings;
  }

  filteredMappings.push({
    tableId,
    targetColumnId: columnId,
    responseField,
    responseFieldName: fieldInfo.displayName,
    responseFieldType: fieldInfo.type,
    matchConfigId: existingMapping?.matchConfigId,
    fixedValue: undefined,
  });

  return filteredMappings;
}

/** What a mapping row writes: a field of the run, or one fixed option. */
export type OutputMappingSource =
  | {
      readonly responseField: string;
      readonly responseFieldName?: string;
      readonly responseFieldType?: string;
    }
  | { readonly fixedValue: string };

export interface AssignOutputColumnInput {
  /** Current mapping array (will not be mutated). */
  readonly prev: ReadonlyArray<OutputMapping>;
  /** Target table id; without it nothing can be stored. */
  readonly tableId: string | undefined;
  /** The column the row writes so far; undefined while it has none. */
  readonly fromColumnId: string | undefined;
  /** The chosen column. Empty string: the row writes nowhere. */
  readonly toColumnId: string;
  /** What the row writes. */
  readonly source: OutputMappingSource;
}

/**
 * The mappings after a row's value got a column. A column takes one value,
 * so the chosen column leaves the row that wrote it so far. The mapping
 * keeps its place in the list; a relation lookup belongs to its column and
 * stays only while the column does.
 */
export function assignOutputColumn(
  input: AssignOutputColumnInput
): OutputMapping[] {
  const { prev, tableId, fromColumnId, toColumnId, source } = input;
  const at = prev.findIndex(m => m.targetColumnId === fromColumnId);
  const existing = at >= 0 ? prev[at] : undefined;
  const next = prev.filter(
    (m, index) => index !== at && m.targetColumnId !== toColumnId
  );
  if (!toColumnId || !tableId) return next;

  const mapping: OutputMapping =
    'fixedValue' in source
      ? {
          tableId,
          targetColumnId: toColumnId,
          responseField: '',
          fixedValue: source.fixedValue,
        }
      : {
          tableId,
          targetColumnId: toColumnId,
          responseField: source.responseField,
          responseFieldName: source.responseFieldName,
          responseFieldType: source.responseFieldType,
          matchConfigId:
            fromColumnId === toColumnId ? existing?.matchConfigId : undefined,
          fixedValue: undefined,
        };
  // Where the row stood before, counted without the mappings that left.
  const place =
    at >= 0
      ? prev.slice(0, at).filter(m => m.targetColumnId !== toColumnId).length
      : next.length;
  next.splice(place, 0, mapping);
  return next;
}

/** Relation lookups no mapping refers to any more after a change. */
export function droppedMatchConfigIds(
  prev: ReadonlyArray<OutputMapping>,
  next: ReadonlyArray<OutputMapping>
): string[] {
  const kept = new Set(next.map(m => m.matchConfigId).filter(Boolean));
  return prev
    .map(m => m.matchConfigId)
    .filter((id): id is string => !!id && !kept.has(id));
}
